import { useEffect, useMemo, useState } from 'react'
import { FiCheck, FiEdit2, FiThumbsUp, FiTrash2 } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CommentsOn } from '@domain/models/activity-comments'
import type { WebinarSuggestion } from '@domain/models/webinar-suggestions'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import WebinarSuggestionModal from '@presentation/components/sections/webinars/WebinarSuggestionModal'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useWebinarSuggestion } from '@presentation/hooks/useWebinarSuggestion'
import { useWebinarSuggestionsActions } from '@presentation/hooks/useWebinarSuggestionsActions'
import SeoHead from '@presentation/seo/SeoHead'

function formatSubmittedOn(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function WebinarSuggestionCommentsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { suggestionId } = useParams<{ suggestionId: string }>()
  const { user } = useAuthUser()
  const {
    suggestion,
    loading: suggestionLoading,
    error: suggestionError,
    reload: reloadSuggestion,
  } = useWebinarSuggestion(suggestionId)
  const [localSuggestion, setLocalSuggestion] = useState<WebinarSuggestion | null>(null)

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const {
    isSuggestionModalOpen,
    editingSuggestion,
    modalSubmitting,
    modalError,
    actionError,
    activeLikeId,
    activeDeleteId,
    openEditSuggestionModal,
    closeSuggestionModal,
    handleSubmitSuggestion,
    handleToggleSuggestionLike,
    handleDeleteSuggestion,
  } = useWebinarSuggestionsActions({
    user,
    onAuthRequired: openLoginModal,
    onSuggestionsChanged: reloadSuggestion,
  })

  useEffect(() => {
    if (suggestion) {
      setLocalSuggestion(suggestion)
    }
  }, [suggestion])

  const currentSuggestion = localSuggestion
  const canManageSuggestion = useMemo(
    () =>
      currentSuggestion
        ? user?.isAdmin === true || user?.uid === currentSuggestion.submittedByUid
        : false,
    [currentSuggestion, user?.isAdmin, user?.uid],
  )

  if (suggestionLoading) {
    return (
      <>
        <SeoHead
          title="Loading webinar suggestion | Parkinson's Insights"
          canonicalPath={
            suggestionId
              ? `/webinars/suggestions/${suggestionId}/comments`
              : '/webinars/suggestions'
          }
          noindex
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (suggestionError || !currentSuggestion || !suggestionId) {
    return (
      <>
        <SeoHead
          title="Suggestion not found | Parkinson's Insights"
          canonicalPath={
            suggestionId
              ? `/webinars/suggestions/${suggestionId}/comments`
              : '/webinars/suggestions'
          }
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {suggestionError ?? 'Webinar suggestion not found'}
          </p>
          <Link
            to="/webinars/suggestions"
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back to Suggestions
          </Link>
        </div>
      </>
    )
  }

  const isLikePending = activeLikeId === currentSuggestion.uid
  const isDeletePending = activeDeleteId === currentSuggestion.uid
  const liked = currentSuggestion.likedByCurrentUser === true

  return (
    <article className="bg-background">
      <SeoHead
        title={`${currentSuggestion.subject || 'Webinar suggestion'} discussion | Parkinson's Insights`}
        description="Read and join discussion on a community-submitted webinar topic suggestion."
        canonicalPath={`/webinars/suggestions/${currentSuggestion.uid}/comments`}
        noindex
      />
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <Link
          to="/webinars/suggestions"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Suggestions
        </Link>

        {(actionError || modalError) ? (
          <p className="mt-4 text-sm font-semibold text-text-secondary">
            {actionError ?? modalError}
          </p>
        ) : null}

        <section className="mt-5 rounded-xl bg-card-surface p-4 shadow-lg shadow-black/20 ring-1 ring-black/5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
            {currentSuggestion.adminReplyLabel ? (
              <span
                className="mb-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-bold"
                style={{
                  backgroundColor: 'var(--color-green-light)',
                  color: 'var(--color-green)',
                  borderColor: 'var(--color-green)',
                }}
              >
                <FiCheck className="h-4 w-4" aria-hidden="true" />
                {currentSuggestion.adminReplyLabel}
              </span>
            ) : null}
            <h4 className="text-xl font-bold text-text-primary">
              {currentSuggestion.subject}
            </h4>
              <p className="mt-1 text-sm text-text-secondary">
                {currentSuggestion.description}
              </p>
              <p className="mt-2 text-xs font-semibold text-text-muted">
                {currentSuggestion.submittedByName
                  || currentSuggestion.submittedByUid
                  || 'Unknown user'}
                {' · '}
                {formatSubmittedOn(currentSuggestion.submittedOn)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                void handleToggleSuggestionLike(
                  currentSuggestion.uid,
                  currentSuggestion.submittedByUid,
                  currentSuggestion.subject,
                )
              }}
              disabled={isLikePending}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full border border-blue px-3 py-2 text-sm font-semibold transition-colors ${
                liked
                  ? 'bg-blue text-white'
                  : 'bg-surface text-blue hover:bg-blue/10'
              } disabled:cursor-not-allowed disabled:opacity-60`}
              aria-label="Toggle webinar suggestion like"
            >
              <FiThumbsUp className="h-4 w-4" />
              {currentSuggestion.likesCount}
            </button>
          </div>

          {canManageSuggestion ? (
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => openEditSuggestionModal(currentSuggestion)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
                aria-label="Edit webinar suggestion"
              >
                <FiEdit2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleDeleteSuggestion(currentSuggestion)
                }}
                disabled={isDeletePending}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Delete webinar suggestion"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </section>

        <CommentsSection
          targetId={currentSuggestion.uid}
          commentsOn={CommentsOn.WebinarSuggestion}
          onAuthRequired={openLoginModal}
          contentOwnerId={currentSuggestion.submittedByUid}
        />
      </div>

      <WebinarSuggestionModal
        key={`${editingSuggestion?.uid ?? 'edit'}-${isSuggestionModalOpen ? 'open' : 'closed'}`}
        isOpen={isSuggestionModalOpen}
        mode="edit"
        initialValue={
          editingSuggestion
            ? {
                subject: editingSuggestion.subject,
                description: editingSuggestion.description,
                adminReplyLabel: editingSuggestion.adminReplyLabel,
              }
            : undefined
        }
        showAdminReplyLabel={user?.isAdmin === true}
        isSubmitting={modalSubmitting}
        error={modalError}
        onSubmit={(input) => {
          void handleSubmitSuggestion(input)
        }}
        onClose={closeSuggestionModal}
      />
    </article>
  )
}
